"use client";

import React, { useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { CartPricingMode, SVariantDto } from "@/__generated__/sdk";
import VariantPrice from "./VariantPrice";
import ProductVariantAddToCartSection from "./ProductVariantAddToCartSection";
import ErrorMessage from "@/app/(main)/_components/ErrorMessage";
import { useCartItem, useCartPricingMode } from "@/redux/features/cart/hooks";
import { getVariantSellerOffers, SellerOffer } from "../sellerOffers";

type Props = {
  variantToShow: SVariantDto | null | undefined;
  selectedVariant: SVariantDto | null | undefined;
  productId: string;
  scrollTargetElemetId: string;
  handleQuantityChange: (quantity: number, pricingMode?: CartPricingMode) => void;
  isPending: boolean;
  tempError?: string | null;
  selectedAttrs: any;
  attributesComplete: boolean;
  featuredOffer?: SellerOffer | null;
  onGuide?: () => void;
};

export default function ProductMobileBottomBar({
  variantToShow,
  selectedVariant,
  selectedAttrs,
  productId,
  handleQuantityChange,
  isPending,
  tempError,
  scrollTargetElemetId,
  attributesComplete,
  featuredOffer,
  onGuide,
}: Props) {
  const hasSelectedAttrs = Object.entries(selectedAttrs).length > 0;

  const activeVariant = hasSelectedAttrs ? (selectedVariant ?? null) : variantToShow;
  const priceVariant = (featuredOffer?.variant as SVariantDto | undefined) ?? activeVariant;

  const sellerOffers = useMemo(
    () => (featuredOffer ? [featuredOffer] : getVariantSellerOffers(activeVariant)),
    [featuredOffer, activeVariant],
  );
  const cartPricingMode = useCartPricingMode();
  const cartItem = useCartItem(featuredOffer?.variantId ?? activeVariant?.id);

  const needsAttributeChoice = !attributesComplete;
  const activeOffer = featuredOffer ?? sellerOffers.find((offer) => cartItem && offer.mode === cartPricingMode) ?? sellerOffers[0] ?? null;
  const stockLeft = activeOffer?.stockLeft;

  const scrollToTarget = () => {
    const target = document.getElementById(scrollTargetElemetId) ?? document.getElementById("variant-pickers");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleBlockedAdd = () => {
    if (needsAttributeChoice) onGuide?.();
    scrollToTarget();
  };

  useEffect(() => {
    if (!tempError) return;

    const startGlow = (targetElement: HTMLElement) => {
      targetElement.classList.remove("scroll-target-glow");

      void targetElement.offsetWidth;

      targetElement.classList.add("scroll-target-glow");

      targetElement.addEventListener(
        "animationend",
        () => {
          targetElement.classList.remove("scroll-target-glow");
        },
        { once: true },
      );
    };

    const timeoutId = window.setTimeout(() => {
      const targetElement = document.getElementById(scrollTargetElemetId);

      if (!targetElement) return;

      let hasGlowed = false;

      const glowOnce = () => {
        if (hasGlowed) return;

        hasGlowed = true;
        startGlow(targetElement);
      };

      window.addEventListener("scrollend", glowOnce, { once: true });

      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Fallback for browsers without reliable `scrollend` support
      window.setTimeout(glowOnce, 900);
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [tempError, scrollTargetElemetId]);

  if (!priceVariant) return <></>;

  return (
    <>
      {tempError && <ErrorMessage error={tempError} />}

      <Box sx={{ width: "100%", pt: 1, pb: 1 }}>
        {stockLeft != null ? (
          <Typography
            variant="caption"
            color="error.main"
            fontWeight={700}
            sx={{ display: "block", mb: 0.75, lineHeight: 1.3, fontSize: "0.75rem" }}
          >
            تنها {stockLeft.toLocaleString("fa-IR")} عدد باقی مانده
          </Typography>
        ) : null}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <VariantPrice layout="dense" variant={priceVariant} sellerOffer={activeOffer} />
          </Box>

          <Box sx={{ flex: "1 1 160px", minWidth: 0, maxWidth: 260, display: "flex", justifyContent: "flex-end", ml: "auto" }}>
            {needsAttributeChoice ? (
              <Button
                variant="contained"
                disableElevation
                onClick={handleBlockedAdd}
                sx={{
                  height: 40,
                  px: 1.75,
                  borderRadius: 2,
                  backgroundColor: "grey.900",
                  whiteSpace: "nowrap",
                  fontWeight: 800,
                  "&:hover": { backgroundColor: "grey.800" },
                }}
              >
                افزودن به سبد خرید
              </Button>
            ) : (
              <ProductVariantAddToCartSection
                variant={priceVariant}
                productId={productId}
                layout="dense"
                pricingMode={activeOffer?.mode}
                handleQuantityChange={handleQuantityChange}
                isPending={isPending}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
